import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

const FONT = { fontFamily: "'Fredoka', 'Inter', sans-serif" };

const faqItems = [
  {
    q: "How does Pixley membership work?",
    a: "Choose the membership that fits your family. Members get a weekly allowance of Pixley Stars — the creative currency behind stories, characters, and storybooks.",
  },
  {
    q: "How do Pixley Stars work?",
    a: "Stars arrive automatically every week with your plan. Creating stories costs 50–600 stars depending on length, characters cost 25 stars, and storybooks cost 50 stars. Need more? Star packs are available anytime in Settings > Shop.",
  },
  {
    q: "What's the difference between plans?",
    a: "Pro ($14.99/mo): 250 stars/week, stories up to 3 minutes, all languages and formats. Family ($24.99/mo): 650 stars/week, stories up to 5 minutes, watermark-free downloads, and up to 5 child profiles.",
  },
  {
    q: "What happens to my stories if I cancel?",
    a: "They're yours to watch, always. You can view and play everything your family has made even without an active membership — creating new things just needs the membership again.",
  },
  {
    q: "How long does story generation take?",
    a: "Usually 5–15 minutes. You'll get a push notification when your story is ready. You can leave the app and come back — it generates in the background!",
  },
  {
    q: "Can I download my videos?",
    a: "Premium subscribers can download videos directly from any story page. Free users can share stories via link anytime — just tap 'Copy Link' or 'Share' on any story.",
  },
  {
    q: "Is the content safe for kids?",
    a: "Absolutely. Every story, character, and storybook goes through multiple AI safety filters. All content is designed for ages 2–8 and is family-friendly.",
  },
  {
    q: "My story failed — what happened?",
    a: "Sometimes AI generation hits a safety filter or times out. Don't worry — your stars are automatically refunded when this happens. Just try again with a slightly different description.",
  },
  {
    q: "What are Storybooks?",
    a: "Storybooks are illustrated bedtime stories with audio narration. You can choose the reading level (ages 2–8), pick your characters, and even use your own voice as the narrator!",
  },
  {
    q: "Can I cancel my subscription?",
    a: "Yes, anytime! Go to Settings > Profile and manage your subscription. You keep access to all premium features until the end of your billing period.",
  },
  {
    q: "How do I contact support?",
    a: "Email us at team@pixleyai.com — we typically respond within 24 hours. We'd love to hear from you!",
  },
];

const FAQItem = ({ item, isOpen, onToggle }) => (
  <div
    style={{
      background: "#EAF3FF",
      borderRadius: 14,
      overflow: "hidden",
      boxShadow: "0px 1px 0px rgba(11,26,51,0.04)",
    }}
  >
    <button
      onClick={onToggle}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 20px",
        textAlign: "left",
        background: "transparent",
        border: "none",
        cursor: "pointer",
        transition: "background 120ms ease",
        ...FONT,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0,101,248,0.04)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
    >
      <span style={{ color: "#0B1A33", fontSize: 16, fontWeight: 500, paddingRight: 16, fontFamily: "'Fredoka', sans-serif" }}>{item.q}</span>
      <motion.span
        animate={{ rotate: isOpen ? 180 : 0 }}
        transition={{ duration: 0.2 }}
        style={{ color: "#0065F8", flexShrink: 0 }}
      >
        <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
        </svg>
      </motion.span>
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div style={{ padding: "0 20px 16px" }}>
            <p style={{ color: "#334155", fontSize: 15, lineHeight: 1.55, margin: 0, ...FONT }}>
              {item.a}
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const HelpFooter = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);
  const faqRef = useRef(null);

  const toggleFAQ = () => {
    const opening = !isOpen;
    setIsOpen(opening);
    if (opening) {
      setTimeout(() => {
        const el = faqRef.current;
        if (el) {
          const y = el.getBoundingClientRect().top + window.scrollY - 100;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      }, 350);
    }
  };

  return (
    <div ref={faqRef} style={{ width: "100%", marginTop: 64, marginBottom: 32, padding: "0 16px" }}>
      <div
        style={{
          maxWidth: 1000,
          margin: "0 auto",
          borderRadius: 24,
          overflow: "hidden",
          background: "#FFFDF7",
          boxShadow: "0px 2px 0px rgba(11,26,51,0.06), 0px 10px 24px rgba(11,26,51,0.08)",
        }}
      >
        {/* Header bar — entire bar is clickable */}
        <div
          onClick={toggleFAQ}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 24px",
            cursor: "pointer",
            transition: "background 120ms ease",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0,101,248,0.03)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 18 }}>💬</span>
            <span style={{ color: "#0B1A33", fontWeight: 600, fontSize: 18, fontFamily: "'Fredoka', sans-serif" }}>
              Need help?
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              onClick={(e) => e.stopPropagation()}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 16px",
                borderRadius: 999,
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                background: isOpen ? "rgba(0,101,248,0.1)" : "#EAF3FF",
                border: "2px solid rgba(0,101,248,0.15)",
                color: "#0065F8",
                boxShadow: "0px 2px 0px rgba(11,26,51,0.06)",
                transition: "all 120ms ease",
                ...FONT,
              }}
            >
              <span>FAQ</span>
              <motion.span
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                style={{ fontSize: 12 }}
              >
                ▾
              </motion.span>
            </button>
            <a
              href="mailto:team@pixleyai.com"
              onClick={(e) => e.stopPropagation()}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 16px",
                borderRadius: 999,
                fontSize: 13,
                fontWeight: 700,
                textDecoration: "none",
                background: "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)",
                color: "#FFF",
                boxShadow: "0px 3px 0px #073B8E",
                transition: "transform 120ms cubic-bezier(.2,.8,.2,1), box-shadow 120ms cubic-bezier(.2,.8,.2,1)",
                ...FONT,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0px 4px 0px #073B8E"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0px 3px 0px #073B8E"; }}
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Email Us
            </a>
          </div>
        </div>

        {/* FAQ accordion */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div style={{ padding: "0 24px 24px", display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ height: 1, background: "rgba(11,26,51,0.06)", marginBottom: 8 }} />
                {faqItems.map((item, idx) => (
                  <FAQItem
                    key={idx}
                    item={item}
                    isOpen={openFAQ === idx}
                    onToggle={() => setOpenFAQ(openFAQ === idx ? null : idx)}
                  />
                ))}
                <div style={{ paddingTop: 12, textAlign: "center" }}>
                  <p style={{ color: "#334155", opacity: 0.5, fontSize: 12, margin: 0, ...FONT }}>
                    Can't find your answer? Email us at{" "}
                    <a href="mailto:team@pixleyai.com" style={{ color: "#0065F8", textDecoration: "underline" }}>
                      team@pixleyai.com
                    </a>
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default HelpFooter;
